import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from './auth';
import { useLanguage, languages } from './i18n';
import { Button } from './components/ui/button';
import { CraftFoodLogo } from './components/brand/Logos';
import LegalFooter from './components/LegalFooter';
import {
  LayoutDashboard, Package, ShoppingCart, Building2,
  Users, FileText, LogOut, Globe, Factory, Tags, ListOrdered, Repeat, Wallet,
  TrendingUp, Sparkles, MessageSquare, Award, Tag, Box, UserCog, Share2,
  Settings as SettingsIcon, Scale
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from './components/ui/dropdown-menu';

export default function Layout({ children }) {
  const { user, logout, isAdmin, isOpsManager, isClient } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/companies/pending/count`);
        if (mounted) setPendingCount(res.data?.count || 0);
      } catch (_) { /* ignore */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, [isAdmin, location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const adminMenuItems = [
    { path: '/admin', label: t('dashboard'), icon: LayoutDashboard },
    { path: '/admin/pending', label: 'Новые регистрации', icon: UserCog, badge: pendingCount },
    { path: '/admin/invite', label: 'Пригласить партнёра', icon: Share2 },
    { path: '/admin/analytics', label: 'Аналитика', icon: TrendingUp },
    { path: '/admin/orders', label: t('orders'), icon: ShoppingCart },
    { path: '/admin/companies', label: t('companies'), icon: Building2 },
    { path: '/admin/segments', label: 'Сегменты', icon: Sparkles },
    { path: '/admin/crm', label: 'CRM', icon: MessageSquare },
    { path: '/admin/loyalty', label: 'Лояльность', icon: Award },
    { path: '/admin/promo-codes', label: 'Промокоды', icon: Tag },
    { path: '/admin/sms', label: 'SMS', icon: MessageSquare },
    { path: '/admin/products', label: t('products'), icon: Package },
    { path: '/admin/categories', label: t('productCategories'), icon: Tags },
    { path: '/admin/ingredients', label: 'Склад', icon: Box },
    { path: '/admin/employees', label: 'Сотрудники', icon: UserCog },
    { path: '/admin/users', label: t('users'), icon: Users },
    { path: '/admin/settings', label: 'Реквизиты компании', icon: SettingsIcon },
    { path: '/admin/legal', label: 'Юр. документы', icon: Scale },
  ];

  const opsMenuItems = [
    { path: '/operations', label: t('production'), icon: Factory },
    { path: '/operations/orders', label: t('orders'), icon: ListOrdered },
  ];

  const clientMenuItems = [
    { path: '/client', label: t('dashboard'), icon: LayoutDashboard },
    { path: '/client/new-order', label: t('newOrder'), icon: ShoppingCart },
    { path: '/client/orders', label: t('orderHistory'), icon: ListOrdered },
    { path: '/client/repeat', label: t('repeatOrder'), icon: Repeat },
    { path: '/client/documents', label: t('documents'), icon: FileText },
    { path: '/client/balance', label: t('outstandingBalance'), icon: Wallet },
  ];

  let menuItems = [];
  if (isAdmin) menuItems = adminMenuItems;
  else if (isOpsManager) menuItems = opsMenuItems;
  else if (isClient) menuItems = clientMenuItems;

  const currentLang = languages.find(l => l.code === language);

  return (
    <div className="min-h-screen bg-[#F6F4EE] text-[#1A1A1A]">
      {/* Top header */}
      <header className="bg-[#F6F4EE]/90 backdrop-blur-sm border-b border-[#1A1A1A]/10 sticky top-0 z-50">
        <div className="px-4 lg:px-8 h-16 flex items-center justify-between">
          <CraftFoodLogo size="sm" />
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="lang-selector" className="h-9 gap-1.5 text-[#1A1A1A]">
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline">{currentLang?.flag} {currentLang?.name}</span>
                  <span className="sm:hidden">{currentLang?.flag}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map(lang => (
                  <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)} data-testid={`lang-${lang.code}`}>
                    <span className="mr-2">{lang.flag}</span> {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="user-menu" className="h-9 border-[#1A1A1A]/20">
                  <span className="hidden sm:inline mr-1 text-[#1A1A1A]">{user?.name}</span>
                  <span className="sm:hidden text-xs">{user?.name?.substring(0, 2)}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled className="opacity-60">{user?.email}</DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="cursor-pointer text-[#8E2A4E]">
                  <LogOut className="h-4 w-4 mr-2" /> {t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 bg-[#F2EFE6]/50 border-r border-[#1A1A1A]/8 min-h-[calc(100vh-65px)] sticky top-[65px]">
          <nav className="p-4 space-y-1">
            {menuItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                  className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#1A1A1A] text-[#F6F4EE]'
                      : 'text-[#1A1A1A]/70 hover:bg-[#1A1A1A]/5 hover:text-[#1A1A1A]'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </span>
                  {item.badge > 0 && (
                    <span
                      data-testid={`nav-badge-${item.path.replace(/\//g, '-')}`}
                      className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                        isActive ? 'bg-[#C9A352] text-[#1A1A1A]' : 'bg-[#8E2A4E] text-[#F6E9E5]'
                      }`}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#F6F4EE] border-t border-[#1A1A1A]/10 z-40">
          <div className="flex items-center justify-around p-2">
            {menuItems.slice(0, 5).map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-nav-${item.path.replace(/\//g, '-')}`}
                  className={`flex flex-col items-center gap-1 px-2 py-1.5 rounded-md min-w-[60px] relative ${
                    isActive ? 'text-[#1A1A1A]' : 'text-[#1A1A1A]/50'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-[10px] font-medium leading-none text-center">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="absolute top-0 right-1 bg-[#8E2A4E] text-[#F6E9E5] text-[9px] rounded-full px-1.5 py-0.5 font-bold">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-10 pb-24 lg:pb-10 min-w-0">{children}</main>
      </div>
      {/* Legal footer (required by Korean PG) */}
      <LegalFooter dark={false} />
    </div>
  );
}
